import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  message,
  Tooltip,
  Progress,
  Badge,
  Tabs,
  List,
  Drawer,
  InputNumber,
  Divider,
  Alert,
  Switch,
  Upload,
  Radio,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  MailOutlined,
  UserOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  TeamOutlined,
  LineChartOutlined,
  DashboardOutlined,
  BellOutlined,
  SettingOutlined,
  ScheduleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface Campaign {
  id: string;
  name: string;
  subject: string;
  type: 'newsletter' | 'promotional' | 'automated' | 'follow-up' | 'welcome' | 'announcement';
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled';
  content: string;
  template: string;
  recipientList: string;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  clickedCount: number;
  bouncedCount: number;
  unsubscribedCount: number;
  scheduledDate?: string;
  sentDate?: string;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  tags: string[];
  automationTrigger?: string;
  isRecurring: boolean;
  recurringFrequency?: 'daily' | 'weekly' | 'monthly';
  segmentation: string[];
}

interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  thumbnail: string;
  content: string;
}

const EmailCampaignManagement: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [form] = Form.useForm();

  // Mock data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockCampaigns: Campaign[] = [
        {
          id: '1',
          name: 'Q2 Product Update Newsletter',
          subject: 'Exciting New Features in Our Latest Release',
          type: 'newsletter',
          status: 'sent',
          content: 'Welcome to our Q2 product update...',
          template: 'newsletter-template-1',
          recipientList: 'All Customers',
          totalRecipients: 1250,
          sentCount: 1250,
          deliveredCount: 1235,
          openedCount: 456,
          clickedCount: 123,
          bouncedCount: 15,
          unsubscribedCount: 8,
          sentDate: '2024-06-20T09:00:00',
          createdBy: 'Sarah Johnson',
          createdDate: '2024-06-18',
          lastModified: '2024-06-20',
          tags: ['newsletter', 'product-update', 'q2'],
          isRecurring: true,
          recurringFrequency: 'monthly',
          segmentation: ['customers', 'active-users']
        },
        {
          id: '2',
          name: 'Welcome Series - New Leads',
          subject: 'Welcome to [Company Name] - Get Started Today!',
          type: 'welcome',
          status: 'draft',
          content: 'Thank you for your interest in our products...',
          template: 'welcome-template-1',
          recipientList: 'New Leads',
          totalRecipients: 0,
          sentCount: 0,
          deliveredCount: 0,
          openedCount: 0,
          clickedCount: 0,
          bouncedCount: 0,
          unsubscribedCount: 0,
          createdBy: 'Mike Chen',
          createdDate: '2024-06-24',
          lastModified: '2024-06-24',
          tags: ['welcome', 'automation', 'leads'],
          automationTrigger: 'lead-created',
          isRecurring: false,
          segmentation: ['new-leads']
        },
        {
          id: '3',
          name: 'Summer Sale Promotion',
          subject: '🌞 Summer Sale: Save 30% on All Products!',
          type: 'promotional',
          status: 'scheduled',
          content: 'Don\'t miss our biggest sale of the summer...',
          template: 'promotional-template-2',
          recipientList: 'Active Customers',
          totalRecipients: 890,
          sentCount: 0,
          deliveredCount: 0,
          openedCount: 0,
          clickedCount: 0,
          bouncedCount: 0,
          unsubscribedCount: 0,
          scheduledDate: '2024-07-01T10:00:00',
          createdBy: 'Alex Thompson',
          createdDate: '2024-06-22',
          lastModified: '2024-06-24',
          tags: ['promotion', 'sale', 'summer'],
          isRecurring: false,
          segmentation: ['active-customers', 'high-value']
        }
      ];
      setCampaigns(mockCampaigns);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    const colors = {
      'draft': 'default',
      'scheduled': 'processing',
      'sending': 'warning',
      'sent': 'success',
      'paused': 'orange',
      'cancelled': 'error'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getTypeColor = (type: string) => {
    const colors = {
      'newsletter': 'blue',
      'promotional': 'green',
      'automated': 'purple',
      'follow-up': 'orange',
      'welcome': 'cyan',
      'announcement': 'red'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const calculateMetrics = (campaign: Campaign) => {
    const openRate = campaign.sentCount > 0 ? (campaign.openedCount / campaign.deliveredCount * 100) : 0;
    const clickRate = campaign.openedCount > 0 ? (campaign.clickedCount / campaign.openedCount * 100) : 0;
    const bounceRate = campaign.sentCount > 0 ? (campaign.bouncedCount / campaign.sentCount * 100) : 0;
    const unsubscribeRate = campaign.sentCount > 0 ? (campaign.unsubscribedCount / campaign.sentCount * 100) : 0;

    return { openRate, clickRate, bounceRate, unsubscribeRate };
  };

  const handleAddCampaign = () => {
    setSelectedCampaign(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    form.setFieldsValue({
      ...campaign,
      scheduledDate: campaign.scheduledDate ? dayjs(campaign.scheduledDate) : null,
    });
    setModalVisible(true);
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setDetailsDrawerVisible(true);
  };

  const handleSaveCampaign = async (values: any) => {
    try {
      const campaignData = {
        ...values,
        id: selectedCampaign?.id || Date.now().toString(),
        scheduledDate: values.scheduledDate ? values.scheduledDate.toISOString() : null,
        lastModified: dayjs().toISOString(),
        totalRecipients: 0,
        sentCount: 0,
        deliveredCount: 0,
        openedCount: 0,
        clickedCount: 0,
        bouncedCount: 0,
        unsubscribedCount: 0
      };

      if (selectedCampaign) {
        setCampaigns(campaigns.map(campaign => 
          campaign.id === selectedCampaign.id ? { ...selectedCampaign, ...campaignData } : campaign
        ));
        message.success('Campaign updated successfully');
      } else {
        setCampaigns([...campaigns, campaignData]);
        message.success('Campaign created successfully');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save campaign');
    }
  };

  const handleSendCampaign = (campaignId: string) => {
    Modal.confirm({
      title: 'Send Campaign',
      content: 'Are you sure you want to send this campaign? This action cannot be undone.',
      onOk: () => {
        setCampaigns(campaigns.map(campaign => 
          campaign.id === campaignId 
            ? { 
                ...campaign, 
                status: 'sending',
                sentDate: dayjs().toISOString()
              }
            : campaign
        ));
        message.success('Campaign is being sent');
        
        // Simulate sending process
        setTimeout(() => {
          setCampaigns(prevCampaigns => prevCampaigns.map(campaign => 
            campaign.id === campaignId 
              ? { 
                  ...campaign, 
                  status: 'sent',
                  sentCount: campaign.totalRecipients,
                  deliveredCount: Math.floor(campaign.totalRecipients * 0.95),
                  openedCount: Math.floor(campaign.totalRecipients * 0.25),
                  clickedCount: Math.floor(campaign.totalRecipients * 0.08),
                  bouncedCount: Math.floor(campaign.totalRecipients * 0.05),
                  unsubscribedCount: Math.floor(campaign.totalRecipients * 0.01)
                }
              : campaign
          ));
          message.success('Campaign sent successfully');
        }, 3000);
      }
    });
  };

  const columns: ColumnsType<Campaign> = [
    {
      title: 'Campaign',
      key: 'campaign',
      width: 250,
      render: (_, record) => (
        <div>
          <div>
            <Text strong>{record.name}</Text>
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
            {record.subject}
          </div>
          <div style={{ marginTop: 4 }}>
            <Tag color={getTypeColor(record.type)}>
              {record.type.toUpperCase()}
            </Tag>
            {record.isRecurring && (
              <Tag color="purple" style={{ marginLeft: 4 }}>
                RECURRING
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 120,
      render: (_, record) => (
        <div>
          <Text strong>{record.totalRecipients.toLocaleString()}</Text>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {record.recipientList}
          </div>
        </div>
      ),
      sorter: (a, b) => a.totalRecipients - b.totalRecipients,
    },
    {
      title: 'Performance',
      key: 'performance',
      width: 180,
      render: (_, record) => {
        const metrics = calculateMetrics(record);
        return (
          <div>
            {record.status === 'sent' ? (
              <div>
                <div style={{ fontSize: '12px' }}>
                  Open: <Text strong>{metrics.openRate.toFixed(1)}%</Text>
                </div>
                <div style={{ fontSize: '12px' }}>
                  Click: <Text strong>{metrics.clickRate.toFixed(1)}%</Text>
                </div>
                <Progress 
                  percent={metrics.openRate} 
                  size="small" 
                  showInfo={false}
                  strokeColor="#1890ff"
                />
              </div>
            ) : (
              <Text type="secondary">-</Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Scheduled/Sent',
      key: 'date',
      width: 140,
      render: (_, record) => (
        <div>
          {record.status === 'scheduled' && record.scheduledDate ? (
            <div>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              {dayjs(record.scheduledDate).format('MMM DD, HH:mm')}
            </div>
          ) : record.sentDate ? (
            <div>
              <CheckCircleOutlined style={{ marginRight: 4, color: '#52c41a' }} />
              {dayjs(record.sentDate).format('MMM DD, HH:mm')}
            </div>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (createdBy) => (
        <Space>
          <UserOutlined />
          {createdBy}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEditCampaign(record)}
              disabled={record.status === 'sent'}
            />
          </Tooltip>
          {record.status === 'draft' && (
            <Tooltip title="Send Now">
              <Button 
                type="link" 
                icon={<SendOutlined />}
                onClick={() => handleSendCampaign(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Duplicate">
            <Button type="link" icon={<CopyOutlined />} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredCampaigns = campaigns.filter(campaign => {
    if (activeTab === 'sent' && campaign.status !== 'sent') return false;
    if (activeTab === 'scheduled' && campaign.status !== 'scheduled') return false;
    if (activeTab === 'draft' && campaign.status !== 'draft') return false;
    if (activeTab === 'automated' && campaign.type !== 'automated') return false;
    return true;
  });

  const campaignStats = {
    total: campaigns.length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    totalRecipients: campaigns.reduce((sum, campaign) => sum + campaign.totalRecipients, 0),
    totalOpens: campaigns.reduce((sum, campaign) => sum + campaign.openedCount, 0),
    totalClicks: campaigns.reduce((sum, campaign) => sum + campaign.clickedCount, 0),
    averageOpenRate: campaigns.filter(c => c.status === 'sent').length > 0 
      ? campaigns.filter(c => c.status === 'sent').reduce((sum, campaign) => 
          sum + (campaign.deliveredCount > 0 ? campaign.openedCount / campaign.deliveredCount * 100 : 0), 0
        ) / campaigns.filter(c => c.status === 'sent').length
      : 0
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <MailOutlined style={{ marginRight: 8 }} />
            Email Campaign Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Button 
              type={viewMode === 'table' ? 'primary' : 'default'}
              onClick={() => setViewMode('table')}
            >
              Table View
            </Button>
            <Button 
              type={viewMode === 'cards' ? 'primary' : 'default'}
              onClick={() => setViewMode('cards')}
            >
              Card View
            </Button>
            <Button icon={<UploadOutlined />}>Import List</Button>
            <Button icon={<SettingOutlined />}>Templates</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCampaign}>
              Create Campaign
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Campaigns"
              value={campaignStats.total}
              prefix={<MailOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Sent Campaigns"
              value={campaignStats.sent}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Recipients"
              value={campaignStats.totalRecipients}
              prefix={<TeamOutlined />}
              formatter={(value) => Number(value).toLocaleString()}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Opens"
              value={campaignStats.totalOpens}
              prefix={<EyeOutlined />}
              formatter={(value) => Number(value).toLocaleString()}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Total Clicks"
              value={campaignStats.totalClicks}
              prefix={<LineChartOutlined />}
              formatter={(value) => Number(value).toLocaleString()}
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card size="small">
            <Statistic
              title="Avg. Open Rate"
              value={campaignStats.averageOpenRate}
              suffix="%"
              prefix={<BarChartOutlined />}
              precision={1}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Campaign Categories Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }}>
        <TabPane tab={`All (${campaigns.length})`} key="all" />
        <TabPane tab={`Sent (${campaigns.filter(c => c.status === 'sent').length})`} key="sent" />
        <TabPane tab={`Scheduled (${campaigns.filter(c => c.status === 'scheduled').length})`} key="scheduled" />
        <TabPane tab={`Draft (${campaigns.filter(c => c.status === 'draft').length})`} key="draft" />
        <TabPane tab={`Automated (${campaigns.filter(c => c.type === 'automated').length})`} key="automated" />
      </Tabs>

      {/* Campaigns Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredCampaigns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            //pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: [10, 20, 50, 100, 500],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} campaigns`,
          }}
        />
      </Card>

      {/* Add/Edit Campaign Modal */}
      <Modal
        title={selectedCampaign ? 'Edit Campaign' : 'Create New Campaign'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={900}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveCampaign}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="name"
                label="Campaign Name"
                rules={[{ required: true, message: 'Please enter campaign name' }]}
              >
                <Input placeholder="e.g., Q2 Product Update Newsletter" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="subject"
                label="Email Subject"
                rules={[{ required: true, message: 'Please enter email subject' }]}
              >
                <Input placeholder="e.g., Exciting New Features in Our Latest Release" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="type"
                label="Campaign Type"
                rules={[{ required: true, message: 'Please select campaign type' }]}
              >
                <Select>
                  <Option value="newsletter">Newsletter</Option>
                  <Option value="promotional">Promotional</Option>
                  <Option value="automated">Automated</Option>
                  <Option value="follow-up">Follow-up</Option>
                  <Option value="welcome">Welcome</Option>
                  <Option value="announcement">Announcement</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="recipientList"
                label="Recipient List"
                rules={[{ required: true, message: 'Please select recipient list' }]}
              >
                <Select>
                  <Option value="All Customers">All Customers</Option>
                  <Option value="New Leads">New Leads</Option>
                  <Option value="Active Customers">Active Customers</Option>
                  <Option value="Inactive Customers">Inactive Customers</Option>
                  <Option value="VIP Customers">VIP Customers</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="template"
                label="Email Template"
                rules={[{ required: true, message: 'Please select template' }]}
              >
                <Select>
                  <Option value="newsletter-template-1">Newsletter Template 1</Option>
                  <Option value="promotional-template-1">Promotional Template 1</Option>
                  <Option value="welcome-template-1">Welcome Template 1</Option>
                  <Option value="custom-template">Custom Template</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select>
                  <Option value="draft">Draft</Option>
                  <Option value="scheduled">Scheduled</Option>
                  <Option value="sent">Sent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="createdBy"
                label="Created By"
                rules={[{ required: true, message: 'Please select creator' }]}
              >
                <Select>
                  <Option value="Sarah Johnson">Sarah Johnson</Option>
                  <Option value="Mike Chen">Mike Chen</Option>
                  <Option value="Alex Thompson">Alex Thompson</Option>
                  <Option value="Lisa Wang">Lisa Wang</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="scheduledDate" label="Scheduled Date & Time">
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="content" label="Email Content">
            <TextArea 
              rows={6} 
              placeholder="Enter your email content here..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="tags" label="Tags">
                <Select mode="multiple" placeholder="Add tags">
                  <Option value="newsletter">Newsletter</Option>
                  <Option value="promotion">Promotion</Option>
                  <Option value="product-update">Product Update</Option>
                  <Option value="welcome">Welcome</Option>
                  <Option value="automation">Automation</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="segmentation" label="Segmentation">
                <Select mode="multiple" placeholder="Select segments">
                  <Option value="customers">Customers</Option>
                  <Option value="leads">Leads</Option>
                  <Option value="active-users">Active Users</Option>
                  <Option value="new-leads">New Leads</Option>
                  <Option value="high-value">High Value</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="isRecurring" valuePropName="checked">
                <Checkbox>Recurring Campaign</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="recurringFrequency" label="Frequency">
                <Select placeholder="Select frequency">
                  <Option value="daily">Daily</Option>
                  <Option value="weekly">Weekly</Option>
                  <Option value="monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedCampaign ? 'Update Campaign' : 'Create Campaign'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button icon={<EyeOutlined />}>Preview</Button>
              <Button icon={<SendOutlined />}>Test Send</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Campaign Details Drawer */}
      <Drawer
        title={selectedCampaign?.name || 'Campaign Details'}
        width={700}
        open={detailsDrawerVisible}
        onClose={() => setDetailsDrawerVisible(false)}
      >
        {selectedCampaign && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small" title="Status">
                  <div style={{ textAlign: 'center' }}>
                    <Tag color={getStatusColor(selectedCampaign.status)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {selectedCampaign.status.toUpperCase()}
                    </Tag>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Type">
                  <div style={{ textAlign: 'center' }}>
                    <Tag color={getTypeColor(selectedCampaign.type)} style={{ fontSize: '14px', padding: '4px 12px' }}>
                      {selectedCampaign.type.toUpperCase()}
                    </Tag>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small" title="Recipients">
                  <div style={{ textAlign: 'center' }}>
                    <Statistic
                      value={selectedCampaign.totalRecipients}
                      formatter={(value) => Number(value).toLocaleString()}
                      valueStyle={{ fontSize: '18px', color: '#1890ff' }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider orientation="left">Campaign Information</Divider>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <p><strong>Subject:</strong> {selectedCampaign.subject}</p>
                <p><strong>Recipient List:</strong> {selectedCampaign.recipientList}</p>
                <p><strong>Template:</strong> {selectedCampaign.template}</p>
                <p><strong>Created By:</strong> {selectedCampaign.createdBy}</p>
              </Col>
              <Col span={12}>
                <p><strong>Created:</strong> {dayjs(selectedCampaign.createdDate).format('MMMM DD, YYYY')}</p>
                {selectedCampaign.scheduledDate && (
                  <p><strong>Scheduled:</strong> {dayjs(selectedCampaign.scheduledDate).format('MMMM DD, YYYY HH:mm')}</p>
                )}
                {selectedCampaign.sentDate && (
                  <p><strong>Sent:</strong> {dayjs(selectedCampaign.sentDate).format('MMMM DD, YYYY HH:mm')}</p>
                )}
                <p><strong>Recurring:</strong> {selectedCampaign.isRecurring ? 'Yes' : 'No'}</p>
              </Col>
            </Row>

            {selectedCampaign.status === 'sent' && (
              <>
                <Divider orientation="left">Performance Metrics</Divider>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={6}>
                    <Card size="small">
                      <Statistic
                        title="Delivered"
                        value={selectedCampaign.deliveredCount}
                        suffix={`/ ${selectedCampaign.sentCount}`}
                        valueStyle={{ fontSize: '16px' }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small">
                      <Statistic
                        title="Opened"
                        value={calculateMetrics(selectedCampaign).openRate}
                        suffix="%"
                        precision={1}
                        valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small">
                      <Statistic
                        title="Clicked"
                        value={calculateMetrics(selectedCampaign).clickRate}
                        suffix="%"
                        precision={1}
                        valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                      />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card size="small">
                      <Statistic
                        title="Bounced"
                        value={calculateMetrics(selectedCampaign).bounceRate}
                        suffix="%"
                        precision={1}
                        valueStyle={{ fontSize: '16px', color: '#ff4d4f' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            <Divider orientation="left">Email Content</Divider>
            <div style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
              <Text>{selectedCampaign.content}</Text>
            </div>

            <Divider orientation="left">Tags & Segmentation</Divider>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Tags:</strong></p>
              <Space wrap>
                {selectedCampaign.tags.map(tag => (
                  <Tag key={tag} color="blue">{tag}</Tag>
                ))}
              </Space>
            </div>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Segmentation:</strong></p>
              <Space wrap>
                {selectedCampaign.segmentation.map(segment => (
                  <Tag key={segment} color="green">{segment}</Tag>
                ))}
              </Space>
            </div>

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" onClick={() => handleEditCampaign(selectedCampaign)}>
                  Edit Campaign
                </Button>
                <Button icon={<CopyOutlined />}>Duplicate</Button>
                <Button icon={<DownloadOutlined />}>Export Report</Button>
                {selectedCampaign.status === 'draft' && (
                  <Button 
                    icon={<SendOutlined />}
                    onClick={() => handleSendCampaign(selectedCampaign.id)}
                  >
                    Send Now
                  </Button>
                )}
              </Space>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default EmailCampaignManagement;
